import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Stack } from '@esgian/esgianui';
import moment from 'moment';
import { ProfileLink } from '@components/Links';

import {
  DRAUGHT_ON_ARRIVAL_DEPARTURE_TEXT,
  LAST_PORT_CALLS_RECORDS_TEXT,
  LESS_THAN_250_RECORDS,
  OPERATOR_ACTIVITY_DEPARTURE_TITLE,
  OPERATOR_ACTIVITY_ARRIVAL_TITLE,
  OPERATOR_ACTIVITY_TIME_TITLE
} from '@constants/tooltips';
import DownloadDateRangeModal from '@components/Inputs/DownloadDateRangeModal/DownloadDateRangeModal';
import { useTimezone } from '@hooks/useTimezone';
import TableV2 from '@components/Tables/TableV2';
import { TextWithTooltipIcon } from '@components';
import { GEOGRAPHICAL_TYPES } from '@constants';

function VesselPortCallsTable({
  title,
  loading,
  portCalls,
  hideColumns,
  name,
  vesselImo,
  geoId,
  ownerId,
  oprId,
  geographicalType = GEOGRAPHICAL_TYPES.ALL
}) {
  const [showModal, setShowModal] = useState(false);
  const { getTimeZoneDisplay } = useTimezone();

  const columns = [
    {
      name: 'vesselName',
      label: 'Vessel',
      options: {
        filter: !hideColumns.includes('vesselName'),
        customFilterType: 'multiselect',
        sort: true,
        display: !hideColumns.includes('vesselName'),
        sortThirdClickReset: true,
        customDownloadValue: (value) => {
          return value;
        },
        customBodyRender: (value, { rowData }) => {
          return value !== null ? (
            <ProfileLink profile={'vessel'} name={value} id={rowData[1]} />
          ) : (
            '-'
          );
        }
      }
    },
    {
      name: 'imo',
      label: 'IMO',
      options: {
        display: !hideColumns.includes('imo'),
        download: !hideColumns.includes('imo'),
        sort: true,
        filter: !hideColumns.includes('imo'),
        customFilterType: 'multiselect'
      }
    },
    {
      name: 'operatorShortName',
      label: 'Operator',
      options: {
        display: !hideColumns.includes('operatorName'),
        download: !hideColumns.includes('operatorName'),
        sort: true,
        filter: !hideColumns.includes('operatorName'),
        customFilterType: 'multiselect',
        sortThirdClickReset: true,
        customBodyRender: (value, { rowData }) => {
          return value !== null ? (
            <ProfileLink profile={'operator'} name={value} id={parseInt(rowData[3])} />
          ) : (
            '-'
          );
        }
      }
    },
    {
      name: 'operatorId',
      options: {
        display: false,
        sort: false,
        filter: false,
        download: false
      }
    },
    {
      name: 'portId',
      options: {
        display: false,
        sort: false,
        filter: false,
        download: false
      }
    },
    {
      name: 'portName',
      label: 'Port',
      options: {
        display: !hideColumns.includes('portName'),
        filter: !hideColumns.includes('portName'),
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'multiselect',
        customBodyRender: (value, { rowData }) => {
          return value !== null ? (
            <ProfileLink profile={'port'} name={value} id={rowData[4]} />
          ) : (
            '-'
          );
        }
      }
    },
    {
      name: 'countryId',
      options: {
        display: false,
        sort: false,
        filter: false,
        download: false
      }
    },
    {
      name: 'countryName',
      label: 'Country',
      options: {
        display: !hideColumns.includes('countryName'),
        filter: !hideColumns.includes('countryName'),
        download: !hideColumns.includes('countryName'),
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'multiselect',
        customBodyRender: (value, { rowData }) => {
          return value !== null ? (
            <ProfileLink profile={'country'} name={value} id={rowData[6]} />
          ) : (
            '-'
          );
        }
      }
    },
    {
      name: 'portCode',
      label: 'UNLOCODE',
      options: {
        display: !hideColumns.includes('portCode'),
        sort: true,
        sortThirdClickReset: true,
        filter: !hideColumns.includes('portCode'),
        customFilterType: 'multiselect'
      }
    },

    {
      name: 'startDate',
      label: 'Arrival',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Arrival'}
            tooltipText={OPERATOR_ACTIVITY_ARRIVAL_TITLE}
            labelVariant={'body2'}
          />
        ),
        display: !hideColumns.includes('startDate'),
        sort: true,
        sortThirdClickReset: true,
        sortCompare: (order) => {
          return (obj1, obj2) => {
            let val1 = moment(obj1.data);
            let val2 = moment(obj2.data);
            if (order === 'asc') {
              return val1.isBefore(val2) ? 1 : -1;
            }
            return val1.isAfter(val2) ? 1 : -1;
          };
        },
        customBodyRender: (value) => {
          if (moment(value).isValid()) {
            return getTimeZoneDisplay(value);
          }
          return '-';
        },
        customFilterType: 'dateBetween'
      }
    },

    {
      name: 'imoDraught',
      label: 'IMO Draught',
      options: {
        display: false,
        filter: false,
        download: false
      }
    },
    {
      name: 'arrivalDraught',
      label: 'Draught on arrival',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Draught on arrival'}
            tooltipText={DRAUGHT_ON_ARRIVAL_DEPARTURE_TEXT}
            labelVariant={'body2'}
          />
        ),
        display: !hideColumns.includes('arrivalDraught'),
        filter: !hideColumns.includes('arrivalDraught'),
        download: !hideColumns.includes('arrivalDraught'),
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value, tableMeta) => {
          const imoDraught = tableMeta.rowData[10];
          const percentArrivalDraught = tableMeta.rowData[12];

          const dataToDisplay = value !== null && value !== undefined ? value : '-';
          const draughtToDisplay =
            imoDraught !== null && imoDraught !== undefined ? imoDraught?.toFixed(1) : '-';
          const percentToDisplay =
            percentArrivalDraught !== null && percentArrivalDraught !== undefined
              ? percentArrivalDraught?.toFixed(1)
              : '-';

          return `${dataToDisplay}/${draughtToDisplay} (${percentToDisplay}%)`;
        },
        customDownloadValue: (value, tableMeta) => {
          const { imoDraught, percentArrivalDraught } = tableMeta;
          const dataToDisplay = value !== null && value !== undefined ? value : '-';
          const draughtToDisplay =
            imoDraught !== null && imoDraught !== undefined ? imoDraught?.toFixed(1) : '-';
          const percentToDisplay =
            percentArrivalDraught !== null && percentArrivalDraught !== undefined
              ? percentArrivalDraught?.toFixed(1)
              : '-';

          return `${dataToDisplay}/${draughtToDisplay} (${percentToDisplay}%)`;
        },
        customFilterType: 'minMax'
      }
    },
    {
      name: 'percentArrivalDraught',
      label: 'Arrival draught percentage',
      options: {
        display: false,
        filter: false,
        download: true,
        customBodyRender: (value) => {
          if (value !== undefined && value !== null) {
            return `${value?.toFixed(1)}%`;
          }
          return '-';
        }
      }
    },
    {
      name: 'regardCompleted',
      options: {
        display: false,
        filter: false,
        download: false
      }
    },
    {
      name: 'endDate',
      label: 'Departure',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Departure'}
            tooltipText={OPERATOR_ACTIVITY_DEPARTURE_TITLE}
            labelVariant={'body2'}
          />
        ),
        sortCompare: (order) => {
          return (obj1, obj2) => {
            let val1 = moment(obj1.data);
            let val2 = moment(obj2.data);
            if (order === 'asc') {
              return val1.isAfter(val2) ? 1 : -1;
            }
            return val1.isBefore(val2) ? 1 : -1;
          };
        },
        customBodyRender: (value, tableMeta) => {
          const { rowData } = tableMeta;
          const regardCompleted = rowData[13];

          if (regardCompleted === false) {
            return '-';
          }

          if (moment(value).isValid()) {
            return getTimeZoneDisplay(value);
          }
          return '-';
        },
        customDownloadValue: (value, tableMeta) => {
          const { regardCompleted } = tableMeta;
          if (regardCompleted === false) {
            return '-';
          }
          if (moment(value).isValid()) {
            return getTimeZoneDisplay(value);
          }
          return '-';
        },
        display: !hideColumns.includes('endDate'),
        filter: !hideColumns.includes('endDate'),
        download: !hideColumns.includes('endDate'),
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'dateBetween'
      }
    },
    {
      name: 'departureDraught',
      label: 'Draught on departure',
      options: {
        sort: true,
        sortThirdClickReset: true,
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Draught on departure'}
            tooltipText={DRAUGHT_ON_ARRIVAL_DEPARTURE_TEXT}
            labelVariant={'body2'}
          />
        ),
        customBodyRender: (value, tableMeta) => {
          const imoDraught = tableMeta.rowData[10];
          const percentDepartureDraught = tableMeta.rowData[16];

          const dataToDisplay = value !== null && value !== undefined ? value : '-';
          const draughtToDisplay =
            imoDraught !== null && imoDraught !== undefined ? imoDraught?.toFixed(1) : '-';
          const percentToDisplay =
            percentDepartureDraught !== null && percentDepartureDraught !== undefined
              ? percentDepartureDraught?.toFixed(1)
              : '-';

          return `${dataToDisplay}/${draughtToDisplay} (${percentToDisplay}%)`;
        },
        customDownloadValue: (value, tableMeta) => {
          const { imoDraught, percentDepartureDraught } = tableMeta;
          const dataToDisplay = value !== null && value !== undefined ? value : '-';
          const draughtToDisplay =
            imoDraught !== null && imoDraught !== undefined ? imoDraught?.toFixed(1) : '-';
          const percentToDisplay =
            percentDepartureDraught !== null && percentDepartureDraught !== undefined
              ? percentDepartureDraught?.toFixed(1)
              : '-';

          return `${dataToDisplay}/${draughtToDisplay} (${percentToDisplay}%)`;
        },
        customFilterType: 'minMax',
        display: !hideColumns.includes('departureDraught'),
        filter: !hideColumns.includes('departureDraught'),
        download: !hideColumns.includes('departureDraught')
      }
    },
    {
      name: 'percentDepartureDraught',
      label: 'Departure draught percentage',
      options: {
        display: false,
        filter: false,
        download: false,
        customBodyRender: (value) => {
          if (value !== undefined && value !== null) {
            return `${value?.toFixed(1)}%`;
          }
        }
      }
    },
    {
      name: 'timeInPort',
      label: 'Port Time (days)',
      options: {
        customHeadLabelRender: () => (
          <TextWithTooltipIcon
            label={'Port Time (days)'}
            tooltipText={OPERATOR_ACTIVITY_TIME_TITLE}
            labelVariant={'body2'}
          />
        ),
        display: !hideColumns.includes('timeInPort'),
        sort: true,
        sortThirdClickReset: true,
        customFilterType: 'minMax',
        customBodyRender: (value, tableMeta) => {
          const { rowData } = tableMeta;
          const regardCompleted = rowData[13];
          if (regardCompleted === false) {
            return '-';
          }
          const rounded = value?.toFixed(3);
          return rounded === '0.000' ? '-' : rounded;
        },
        customDownloadValue: (value, tableMeta) => {
          const { regardCompleted } = tableMeta;
          if (regardCompleted === false) {
            return '-';
          }
          const rounded = value?.toFixed(3);
          return rounded === '0.000' ? '-' : rounded;
        }
      }
    }
  ];
  const options = {
    selectableRows: 'none',
    // customToolbar: loadAllToolbar,
    responsive: 'standard',
    filter: false,
    search: false,
    download: false,
    setTableProps: () => {
      return {
        id: 'vessel-port-calls-table'
      };
    },
    onDownload: () => {
      setShowModal(true);
      return false;
    },

    print: false,
    viewColumns: false,
    pagination: true,
    toolbar: false,
    elevation: 0,
    tableBodyMaxHeight: '80vh',
    rowsPerPageOptions: [10, 25, 50, 100],
    sortOrder: {
      name: 'endDate',
      direction: 'desc'
    }
  };

  return (
    <>
      <TableV2
        handleCustomDownload={() => setShowModal(true)}
        loading={loading}
        title={
          <Stack sx={{ pb: 2, pt: 2 }}>
            <Typography id={'vessel-port-calls-title'} variant={'h6'}>
              {title}
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'}>
              {portCalls?.length <= 249
                ? LESS_THAN_250_RECORDS + ' ' + portCalls?.length
                : LAST_PORT_CALLS_RECORDS_TEXT}
            </Typography>
          </Stack>
        }
        data={portCalls?.length ? portCalls : []}
        columns={columns}
        options={options}
      />
      {showModal && (
        <DownloadDateRangeModal
          geographicalType={geographicalType}
          columns={columns}
          showModal={showModal}
          setShowModal={setShowModal}
          vesselImo={vesselImo}
          geoId={geoId}
          ownerId={ownerId}
          oprId={oprId}
          name={name}
        />
      )}
    </>
  );
}

VesselPortCallsTable.propTypes = {
  title: PropTypes.string,
  hideColumns: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  portCalls: PropTypes.arrayOf(PropTypes.object),
  name: PropTypes.string,
  geographicalType: PropTypes.oneOf(Object.values(GEOGRAPHICAL_TYPES)).isRequired,
  vesselImo: PropTypes.number,
  geoId: PropTypes.number,
  ownerId: PropTypes.number,
  oprId: PropTypes.number
};

VesselPortCallsTable.defaultProps = {
  title: 'Most Recent Vessels Port Calls',
  name: '',
  loading: false,
  portCalls: [],
  hideColumns: [],
  vesselImo: null,
  geoId: null,
  ownerId: null,
  oprId: null
};

export default VesselPortCallsTable;
